import { Controller, type Control } from 'react-hook-form';
import { AutocompleteField } from '~/components/AutocompleteField/AutocompleteField.tsx';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import type { TGuaranteesBeneficiary } from '~/store/guarantees/types.ts';
import { GuaranteesBeneficiaryFieldName } from '../../../rootSection/types.ts';

type TResidenceCountryInputChangeReason = 'input' | 'reset' | 'clear' | string;

type TBeneficiaryResidenceCountryFieldProps = {
  control: Control<TGuaranteesBeneficiary>;
  options: TAutocompleteOption[];
  inputValue: string;
  setInputValue: (value: string) => void;
  disabled: boolean;
  loading: boolean;
};

const handleResidenceCountryInputChange = (
  value: string,
  reason: TResidenceCountryInputChangeReason,
  onChange: (value: TAutocompleteOption | null) => void,
  setInputValue: (value: string) => void
) => {
  if (reason === 'input' || reason === 'reset') {
    setInputValue(value);
  }

  if (reason === 'clear') {
    onChange(null);
    setInputValue('');
  }
};

const handleResidenceCountryChange = (
  value: TAutocompleteOption | null,
  onChange: (value: TAutocompleteOption | null) => void,
  setInputValue: (value: string) => void
) => {
  onChange(value);
  setInputValue(value?.label ?? '');
};

export const BeneficiaryResidenceCountryField = ({
  control,
  options,
  inputValue,
  setInputValue,
  disabled,
  loading,
}: TBeneficiaryResidenceCountryFieldProps) => (
  <Controller
    name={GuaranteesBeneficiaryFieldName.BeneficiaryResidenceCountry}
    control={control}
    render={({ field, fieldState, formState }) => {
      const showError = (fieldState.isTouched || formState.isSubmitted) && !!fieldState.error;

      return (
        <AutocompleteField
          options={options}
          value={(field.value ?? null) as TAutocompleteOption | null}
          onChange={(value) =>
            handleResidenceCountryChange(value, field.onChange, setInputValue)
          }
          onBlur={field.onBlur}
          inputRef={field.ref}
          inputValue={inputValue}
          onInputChange={(_, value, reason) =>
            handleResidenceCountryInputChange(value, reason, field.onChange, setInputValue)
          }
          label={guaranteesClientText.beneficiaryResidenceCountryLabel}
          disabled={disabled}
          loading={loading}
          error={showError}
          helperText={showError ? fieldState.error?.message : ''}
          sx={guaranteesClientPageStyles.flowField}
        />
      );
    }}
  />
);
